import { BadgeCheck, ExternalLink } from "lucide-react";
import { useT } from "../i18n/I18nProvider";
import { formatDateFr, formatIndicator } from "../lib/format";

// Single source of truth for category ordering + emojis.
// Used by CityDetail and CostOfLiving "Détail des prix par ville".
export const NUMBEO_CATEGORY_META = [
  { key: "restaurants_sorties",       emoji: "☕", labelKey: "city.categoryRestaurants" },
  { key: "courses",                   emoji: "🛒", labelKey: "city.categoryGroceries" },
  { key: "transports",                emoji: "🚇", labelKey: "city.categoryTransport" },
  { key: "loyers",                    emoji: "🏠", labelKey: "city.categoryRents" },
  { key: "achat_immobilier",          emoji: "🏢", labelKey: "city.categoryBuy" },
  { key: "charges_utilities",         emoji: "⚡", labelKey: "city.categoryUtilities" },
  { key: "garde_enfants_education",   emoji: "👶", labelKey: "city.categoryChildcare" },
  { key: "salaires_finance",          emoji: "💰", labelKey: "city.categorySalary" },
  { key: "sport_loisirs",             emoji: "🏃", labelKey: "city.categoryLeisure" },
];

/**
 * Numbeo attribution footer used under any Numbeo-sourced section.
 * Renders date (if present) + link to Numbeo city page.
 */
export function NumbeoAttribution({ numbeo, testId = "numbeo-attribution" }) {
  const { t } = useT();
  if (!numbeo) return null;
  const lastUpdate = numbeo.meta?.numbeo_last_update;
  return (
    <div
      className="mt-3 flex items-start sm:items-center justify-between gap-3 flex-wrap text-[11px] text-slate-500"
      data-testid={testId}
    >
      <div className="flex items-center gap-1.5">
        <BadgeCheck className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0" />
        <span>
          {t("city.numbeoAttribution")}
          {lastUpdate && (
            <>
              {" · "}
              {t("city.numbeoLastUpdate")} :{" "}
              <span className="font-medium text-slate-700">{formatDateFr(lastUpdate)}</span>
            </>
          )}
        </span>
      </div>
      {numbeo.url && (
        <a
          href={numbeo.url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-[#0056D2] hover:underline"
          data-testid={`${testId}-link`}
        >
          {t("city.numbeoOpen")} <ExternalLink className="w-3 h-3" />
        </a>
      )}
    </div>
  );
}

/**
 * Grid of Numbeo indicators grouped by category.
 * Categories rendered: every entry in NUMBEO_CATEGORY_META that has items
 * in the supplied `indicators` object, in the canonical order, but caller
 * can also restrict via the `categories` prop (array of category keys).
 */
export function NumbeoCategoryGrid({ indicators, categories, testIdPrefix = "category" }) {
  const { t } = useT();
  if (!indicators) return null;
  const keysToRender = categories
    ? NUMBEO_CATEGORY_META.filter((c) => categories.includes(c.key))
    : NUMBEO_CATEGORY_META;

  return (
    <div className="space-y-8" data-testid="numbeo-category-grid">
      {keysToRender.map(({ key, emoji, labelKey }) => {
        const items = indicators[key] || [];
        if (items.length === 0) return null;
        return (
          <div key={key} data-testid={`${testIdPrefix}-${key}`}>
            <h3 className="font-display text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
              <span className="text-xl" aria-hidden="true">{emoji}</span>
              {t(labelKey)}
              <span className="text-xs text-slate-400 font-normal ml-1">({items.length})</span>
            </h3>
            <ul
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3"
              data-testid={`${testIdPrefix}-${key}-list`}
            >
              {items.map((ind) => {
                const { primary, range } = formatIndicator(ind);
                return (
                  <li
                    key={ind.key}
                    className="bg-white border border-slate-200 rounded-xl px-4 py-3 flex items-start justify-between gap-3"
                    data-testid={`indicator-${ind.key}`}
                  >
                    <span className="text-sm text-slate-600 flex-1 leading-snug">{ind.label_fr}</span>
                    <span className="text-right whitespace-nowrap">
                      <span className="block text-sm font-semibold text-slate-900 tabular-nums">{primary}</span>
                      {range && (
                        <span className="block text-[11px] text-slate-400 tabular-nums mt-0.5">({range})</span>
                      )}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })}
    </div>
  );
}
